import { Avatar, Badge, Box, IconButton, SxProps, Theme, Skeleton } from '@mui/material'
import { C1Icon } from 'components/core/icons/C1Icon'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { AppIconUpdateDialog } from 'components/pages/app/details/AppIconUpdate'
import { isC1ManagedApp } from 'lib/constants/system'
import { useSimpleModal } from 'lib/modals'
import { CircleAlert, Pencil, Shapes } from 'lucide-react'
import Image from 'next/image'
import { App, GetAppRequest } from 'pbts/c1/api/app/v1/app'
import { AppIconRef } from 'pbts/c1/api/assets/v1/app_icons'
import c1OnC1Icon from 'public/static/c1-on-c1.svg'
import { FC, useState } from 'react'
import { useIntl } from 'react-intl'
import { getAppIconApi } from 'redux_toolkit/app-icon'
import { appGetApi } from 'redux_toolkit/apps'
import { AvatarSize, getAvatarIconSize } from './AvatarSizes'
import { avatarStyles, avatarIconStyles } from './styles'

export type AppAvatarProps = {
    appName?: string
    iconUrl?: string
    appId?: string
    size?: AvatarSize
    sx?: SxProps
    /** When true (default), image is decorative with empty alt. Set false when used standalone without visible text. */
    decorative?: boolean
    editable?: boolean
}

type AppIconBoxProps = {
    appId: string
    iconSx: SxProps<Theme>
    pixelSize: number
    alt: string
}

const AppIconBox: FC<AppIconBoxProps> = ({ appId, iconSx, pixelSize, alt }) => {
    const { data, isLoading, isError } = getAppIconApi.useMultiGet(AppIconRef.create({ appId }))

    if (data) {
        return <Image
            src={data.src}
            loader={({ width, src }): string => {
                if (!data.srcSet || !data.srcSet.candidates) {
                    return src
                }

                const keys = Object.keys(data.srcSet?.candidates || {}).map(Number).sort((a, b) => b - a)

                for (const key of keys) {
                    if (key >= width) {
                        return data.srcSet?.candidates[key].src || ''
                    }
                }

                const rv = data.srcSet?.candidates[keys[keys.length - 1]].src || ''
                return rv
            }}
            alt={alt}
            width={pixelSize}
            height={pixelSize}
        />
    }

    if (isLoading) { return <Skeleton height={pixelSize} width={pixelSize} /> }
    if (isError) { return <Box sx={iconSx}><C1Icon icon={CircleAlert} sx={{ color: 'error.main' }} /></Box> }

    return <Box sx={iconSx}>
        <C1Icon
            icon={Shapes}
            sx={{ height: '100%', width: '100%', color: 'text.primary' }}
            aria-label={alt || undefined}
            aria-hidden={!alt}
        />
    </Box>
}

export const AppAvatar = ({ appName, iconUrl, appId, size = 'md', sx, editable = false, decorative = false }: AppAvatarProps): React.ReactElement => {
    const [isHovered, setIsHovered] = useState(false)
    const { open } = useSimpleModal()
    const avatarSx = sxc(avatarStyles.avatar, avatarStyles.appAvatar, size && avatarStyles[size], sx)
    const iconSx = avatarIconStyles[`${size}Icon`] || avatarIconStyles.icon
    const pixelSize = getAvatarIconSize(size)
    const alt = decorative ? '' : (appName || '')
    const intl = useIntl()

    const handleEditClick = (): void => {
        if (appId) {
            const app = App.create({ id: appId, displayName: appName })
            open(AppIconUpdateDialog, { app })()
        }
    }

    let avatar: React.ReactElement

    if (appId && isC1ManagedApp(appId)) {
        avatar = <Avatar sx={avatarSx} alt={alt}>
            <Box
                component="img"
                display="block"
                src={c1OnC1Icon.src}
                sx={iconSx}
                alt={alt}
            />
        </Avatar>
    } else if (appId) {
        avatar = <Avatar alt={alt} sx={avatarSx}>
            <AppIconBox
                appId={appId}
                alt={decorative ? '' : (alt || intl.formatMessage({ id: 'default_app_icon', defaultMessage: 'Default app icon' }))}
                iconSx={iconSx}
                pixelSize={pixelSize}
            />
        </Avatar>
    } else if (iconUrl) {
        avatar = <Avatar alt={alt} sx={avatarSx}>
            <Box display="block" component="img" src={iconUrl} sx={iconSx} alt={alt} title={appName} />
        </Avatar>
    } else if (appName) {
        const encodedAppName = encodeURIComponent(appName)

        avatar = <Avatar alt={alt} sx={avatarSx}>
            <Box display="block" component="img" src={`/dapi/static/images/app_by_name/${encodedAppName}`} alt={alt} title={appName} sx={iconSx} />
        </Avatar>
    } else {
        avatar = <Avatar alt={alt} sx={avatarSx}>
            <Box sx={iconSx}>
                <C1Icon
                    icon={Shapes}
                    sx={{ height: '100%', width: '100%', color: 'text.primary' }}
                    aria-label={decorative ? undefined : intl.formatMessage({ id: 'default_app_icon', defaultMessage: 'Default app icon' })}
                />
            </Box>
        </Avatar>
    }

    if (editable && appId) {
        return (
            <Badge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                badgeContent={
                    <IconButton
                        size="small"
                        aria-label={intl.formatMessage({ id: 'edit_app_icon', defaultMessage: 'Edit app icon' })}
                        onClick={(e): void => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleEditClick()
                        }}
                        sx={{
                            backgroundColor: 'primary.main',
                            width: 24,
                            height: 24,
                            opacity: isHovered ? 1 : 0,
                            transition: 'opacity 0.2s',
                            '&:hover, &:focus': {
                                backgroundColor: 'primary.dark',
                                opacity: 1,
                            },
                        }}
                    >
                        <C1Icon icon={Pencil} size="sm" className="text-white" />
                    </IconButton>
                }
                onMouseEnter={(): void => setIsHovered(true)}
                onMouseLeave={(): void => setIsHovered(false)}
            >
                {avatar}
            </Badge>
        )
    }

    return avatar
}

export const AppAvatarWithFetch = ({ appId, size = 'md', sx }: { appId: string, size?: AvatarSize, sx?: SxProps }): React.ReactElement => {
    const { data, isLoading, isError } = appGetApi.useGet(GetAppRequest.create({ id: appId }))
    if (data && !isLoading && !isError) {
        return <AppAvatar appName={data.displayName} appId={appId} size={size} sx={sx} />
    }
    return <AppAvatar size={size} sx={sx} />
}
